// LocaleHandler — TypeScript port of v1's InductVisibilityWebsite-1.0/src/components/handler/LocaleHandler.js.
//
// Ports v1's behavior verbatim:
//   - Asks the parent SCC shell for the user's locale via getLocaleFromParent (200ms timeout).
//   - Falls back to navigator.language when the parent times out OR returns an empty locale
//     (MLS-disabled regions per weblab https://weblab.amazon.com/wl/SCC_MLS_349230).
//   - Normalizes the returned tag (e.g. `de-CH` -> `de`) to one of the 8 supported message
//     buckets (en, es, fr, it, ja, de, ar, tr); unknown -> `en`.
//   - `getDirection`: `ar` uses RTL, everything else LTR.
//   - `getMessages`: returns the message catalog object for the picked locale.
//
// NOTE ON pt.json: v2's locales/ has pt.json (ported from v1 unchanged), but v1's LocaleHandler
// doesn't wire it up. Matching v1 faithfully — leave `pt` unwired.

import intlMessageEN from './locales/en.json';
import intlMessageES from './locales/es.json';
import intlMessageFR from './locales/fr.json';
import intlMessageIT from './locales/it.json';
import intlMessageJP from './locales/ja.json';
import intlMessageDE from './locales/de.json';
import intlMessageAR from './locales/ar.json';
import intlMessageTR from './locales/tr.json';
import { getLocaleFromParent, TIMEOUT_STRING } from '../handler/ParentDataHandler';

const EMPTY_STRING = '';

type MessageCatalog = Record<string, string>;

export async function getLocale(): Promise<string> {
  const locale = await fetchLocale();
  switch (locale) {
    case 'de':
    case 'de-DE':
    case 'de-CH':
    case 'de-AT':
    case 'de-LU':
    case 'de-LI':
      return 'de';
    case 'es':
    case 'es-ES':
    case 'es-AR':
    case 'es-GT':
    case 'es-CR':
    case 'es-PA':
    case 'es-DO':
    case 'es-MX':
    case 'es-VE':
    case 'es-CO':
    case 'es-PE':
    case 'es-EC':
    case 'es-CL':
    case 'es-UY':
    case 'es-PY':
    case 'es-BO':
    case 'es-SV':
    case 'es-HN':
    case 'es-NI':
    case 'es-PR':
      return 'es';
    case 'ja':
    case 'ja-JP':
      return 'ja';
    case 'tr':
    case 'tr-TR':
      return 'tr';
    case 'it':
    case 'it-IT':
    case 'it-CH':
      return 'it';
    case 'fr':
    case 'fr-FR':
    case 'fr-BE':
    case 'fr-CA':
    case 'fr-CH':
    case 'fr-LU':
      return 'fr';
    case 'ar':
    case 'ar-SA':
    case 'ar-IQ':
    case 'ar-EG':
    case 'ar-LY':
    case 'ar-DZ':
    case 'ar-MA':
    case 'ar-TN':
    case 'ar-OM':
    case 'ar-YE':
    case 'ar-SY':
    case 'ar-JO':
    case 'ar-LB':
    case 'ar-KW':
    case 'ar-AE':
    case 'ar-BH':
    case 'ar-QA':
      return 'ar';
    default:
      return 'en';
  }
}

async function fetchLocale(): Promise<string> {
  const localeResponse = await getLocaleFromParent();
  // Parent timed out (v1 fallback: navigator.language).
  if (localeResponse === TIMEOUT_STRING) {
    return navigator.language;
  }
  // Response shape from v1: { locale: <JSON-stringified BCP-47 tag> }.
  const rawLocale = (localeResponse as { locale?: string })?.locale;
  if (typeof rawLocale !== 'string') {
    return navigator.language;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(rawLocale);
  } catch {
    // Some hosts return the tag as a plain string (not JSON-encoded); pass through.
    parsed = rawLocale;
  }
  if (typeof parsed !== 'string' || parsed === EMPTY_STRING) {
    return navigator.language;
  }
  return parsed;
}

export function getDirection(locale: string): 'rtl' | 'ltr' {
  return locale === 'ar' ? 'rtl' : 'ltr';
}

export function getMessages(locale: string): MessageCatalog {
  switch (locale) {
    case 'en':
      return intlMessageEN as MessageCatalog;
    case 'es':
      return intlMessageES as MessageCatalog;
    case 'fr':
      return intlMessageFR as MessageCatalog;
    case 'it':
      return intlMessageIT as MessageCatalog;
    case 'ja':
      return intlMessageJP as MessageCatalog;
    case 'de':
      return intlMessageDE as MessageCatalog;
    case 'ar':
      return intlMessageAR as MessageCatalog;
    case 'tr':
      return intlMessageTR as MessageCatalog;
    default:
      return intlMessageEN as MessageCatalog;
  }
}
